<h2 mat-dialog-title class="dialog-header">
    <mat-icon class="header-icon">account_tree</mat-icon>
    <span class="header-title">Hat Explorer</span>
    
    <!-- View Mode Toggle -->
    <mat-button-toggle-group 
        [value]="viewMode" 
        (change)="setViewMode($event.value)"
        class="view-toggle">
        <mat-button-toggle value="positions" matTooltip="View by Org Part" class="entity-positions">
            <mat-icon>work</mat-icon>
            <span class="toggle-label">Org Parts</span>
        </mat-button-toggle>
        <mat-button-toggle value="hats" matTooltip="View by Hat" class="entity-hats">
            <mat-icon>school</mat-icon>
            <span class="toggle-label">Hats</span>
        </mat-button-toggle>
    </mat-button-toggle-group>

    <!-- Stats Pills -->
    <div class="header-stats">
        <div class="stat-pill entity-positions" matTooltip="Org Parts">
            <mat-icon>work</mat-icon>
            <span>{{ stats.totalPositions }}</span>
        </div>
        <div class="stat-pill entity-hats" matTooltip="Hats">
            <mat-icon>school</mat-icon>
            <span>{{ stats.totalHats }}</span>
        </div>
        <div class="stat-pill entity-users" matTooltip="Staff">
            <mat-icon>people</mat-icon>
            <span>{{ stats.totalUsers }}</span>
        </div>
        <div class="stat-pill entity-policies" matTooltip="Policies">
            <mat-icon>policy</mat-icon>
            <span>{{ stats.totalPolicies }}</span>
        </div>
        <div class="stat-pill entity-security-groups" matTooltip="Security Groups">
            <mat-icon>security</mat-icon>
            <span>{{ stats.totalSecurityGroups }}</span>
        </div>
    </div>

    <span class="spacer"></span>

    <button mat-icon-button mat-dialog-close class="close-button" matTooltip="Close">
        <mat-icon>close</mat-icon>
    </button>
</h2>

<mat-dialog-content class="explorer-content">
    <!-- Search and Controls -->
    <div class="search-controls">
        <div class="search-row">
            <shared-org-search-bar
                searchLabel="Search"
                searchPlaceholder="Search org parts, hats, staff..."
                [showPositionSearch]="true"
                [positionNames]="allPositionNames"
                (searchChanged)="onSearchChanged($event)"
                (positionSearchChanged)="onPositionSearchChanged($event)">
            </shared-org-search-bar>

            <div class="tree-controls">
                <button mat-icon-button (click)="expandAll()" matTooltip="Expand All">
                    <mat-icon>unfold_more</mat-icon>
                </button>
                <button mat-icon-button (click)="collapseAll()" matTooltip="Collapse All">
                    <mat-icon>unfold_less</mat-icon>
                </button>
            </div>
            </div>
        <!-- Mobile control bar: a "Filters" toggle (collapses the chip row) plus a
             compact expand/collapse-all pair. Hidden on desktop (chips show inline). -->
        <div class="mobile-filter-bar">
            <button type="button" class="filters-toggle" mat-stroked-button
                    [class.active]="hasActiveFilter"
                    [attr.aria-expanded]="filtersExpanded"
                    (click)="toggleFilters()">
                <mat-icon class="filter-icon">tune</mat-icon>
                Filters
                <mat-icon class="chevron" [class.open]="filtersExpanded">expand_more</mat-icon>
            </button>
            <div class="tree-controls mobile-tree-controls">
                <button mat-icon-button (click)="expandAll()" matTooltip="Expand All">
                    <mat-icon>unfold_more</mat-icon>
                </button>
                <button mat-icon-button (click)="collapseAll()" matTooltip="Collapse All">
                    <mat-icon>unfold_less</mat-icon>
                </button>
            </div>
        </div>

        <div class="filter-row" [class.collapsed]="!filtersExpanded">
            <button mat-stroked-button
                    [class.active]="!hasActiveFilter"
                    (click)="clearFilters()">
                All ({{ stats.totalHats }})
            </button>
            <button mat-stroked-button 
                    [class.active]="noUsersFilter"
                    (click)="toggleNoUsersFilter()">
                <mat-icon class="filter-icon">person_off</mat-icon>
                No Staff ({{ noUsersCount }})
            </button>
            <button mat-stroked-button 
                    [class.active]="multiUserFilter"
                    (click)="toggleMultiUserFilter()">
                <mat-icon class="filter-icon">group</mat-icon>
                Multi-Staff ({{ multiUserCount }})
            </button>
            <button mat-stroked-button 
                    [class.active]="noPoliciesFilter"
                    (click)="toggleNoPoliciesFilter()">
                <mat-icon class="filter-icon">policy</mat-icon>
                No Policies ({{ noPoliciesCount }})
            </button>
            <button mat-stroked-button 
                    [class.active]="noSgFilter"
                    (click)="toggleNoSgFilter()">
                <mat-icon class="filter-icon">shield</mat-icon>
                No SG ({{ noSgCount }})
            </button>
            <button mat-stroked-button 
                    [class.active]="noPositionsFilter"
                    (click)="toggleNoPositionsFilter()">
                <mat-icon class="filter-icon">wrong_location</mat-icon>
                No Org Parts ({{ noPositionsCount }})
            </button>
            <button mat-stroked-button 
                    [class.active]="postsFilter"
                    (click)="togglePostsFilter()">
                <mat-icon class="filter-icon">work</mat-icon>
                Posts ({{ postsCount }})
            </button>
            <button mat-stroked-button 
                    [class.active]="specialRightsFilter"
                    (click)="toggleSpecialRightsFilter()">
                <mat-icon class="filter-icon">shield</mat-icon>
                Special Rights ({{ specialRightsCount }})
            </button>
        </div>
    </div>

    <!-- Position View Tree -->
    <div class="tree-container" *ngIf="viewMode === 'positions'">
        <div class="view-description">
            <mat-icon>info_outline</mat-icon>
            <span>Org Parts → Hats → Staff, Policies, Security Groups</span>
        </div>

        <mat-tree [dataSource]="positionDataSource" [treeControl]="positionTreeControl" class="explorer-tree">
            <!-- Leaf nodes -->
            <mat-tree-node *matTreeNodeDef="let node" matTreeNodePadding>
                <button mat-icon-button disabled></button>
                <div class="tree-node" [ngClass]="'node-' + node.type" [class.is-post]="node.partType === 'post'" [class.search-match]="node.matchesSearch" [class.special-rights-node]="node.isSpecialRights">
                    <mat-icon class="node-icon">{{ node.icon }}</mat-icon>
                    <span class="node-name">{{ node.name | normalize }}</span>
                    <span class="node-details" *ngIf="node.details">{{ node.details }}</span>
                    <span *ngIf="node.type === 'policy'" 
                          class="policy-status" 
                          [ngClass]="node.status ? 'status-' + node.status : 'status-unknown'">
                        {{ node.status ? getStatusLabel(node.status) : 'N/A' }}
                    </span>
                </div>
                <div class="node-hierarchy" *ngIf="node.hierarchy">↳ {{ node.hierarchy }}</div>
            </mat-tree-node>

            <!-- Expandable nodes -->
            <mat-nested-tree-node *matTreeNodeDef="let node; when: hasChild">
                <div class="mat-tree-node">
                    <button mat-icon-button matTreeNodeToggle [attr.aria-label]="'Toggle ' + node.name">
                        <mat-icon class="toggle-icon">
                            {{ positionTreeControl.isExpanded(node) ? 'expand_more' : 'chevron_right' }}
                        </mat-icon>
                    </button>
                    <div class="tree-node" [ngClass]="'node-' + node.type" [class.is-post]="node.partType === 'post'" [class.search-match]="node.matchesSearch" [class.special-rights-node]="node.isSpecialRights">
                        <mat-icon class="node-icon">{{ node.icon }}</mat-icon>
                        <span class="node-name">{{ node.name | normalize }}</span>
                        <span class="node-details" *ngIf="node.details">{{ node.details }}</span>
                        <span *ngIf="node.type === 'policy'" 
                              class="policy-status" 
                              [ngClass]="node.status ? 'status-' + node.status : 'status-unknown'">
                            {{ node.status ? getStatusLabel(node.status) : 'N/A' }}
                        </span>
                        <span class="child-count" *ngIf="node.children?.length && node.type !== 'label'">
                            ({{ node.children.length }})
                        </span>
                    </div>
                    <div class="node-hierarchy" *ngIf="node.hierarchy">↳ {{ node.hierarchy }}</div>
                </div>
                <div class="nested-children" [class.hidden]="!positionTreeControl.isExpanded(node)">
                    <ng-container matTreeNodeOutlet></ng-container>
                </div>
            </mat-nested-tree-node>
        </mat-tree>

        <div class="empty-state" *ngIf="positionDataSource.data.length === 0">
            <mat-icon>search_off</mat-icon>
            <p *ngIf="searchTerm">No results found for "{{ searchTerm }}"</p>
            <p *ngIf="!searchTerm">No org parts in this organization</p>
        </div>
    </div>

    <!-- Hat View Tree -->
    <div class="tree-container" *ngIf="viewMode === 'hats'">
        <div class="view-description">
            <mat-icon>info_outline</mat-icon>
            <span>Hats → Org Parts, Staff, Policies, Security Groups</span>
        </div>

        <mat-tree [dataSource]="hatDataSource" [treeControl]="hatTreeControl" class="explorer-tree">
            <!-- Leaf nodes -->
            <mat-tree-node *matTreeNodeDef="let node" matTreeNodePadding>
                <button mat-icon-button disabled></button>
                <div class="tree-node" [ngClass]="'node-' + node.type" [class.is-post]="node.partType === 'post'" [class.search-match]="node.matchesSearch" [class.special-rights-node]="node.isSpecialRights">
                    <mat-icon class="node-icon">{{ node.icon }}</mat-icon>
                    <span class="node-name">{{ node.name | normalize }}</span>
                    <span class="node-details" *ngIf="node.details">{{ node.details }}</span>
                    <span *ngIf="node.type === 'policy'" 
                          class="policy-status" 
                          [ngClass]="node.status ? 'status-' + node.status : 'status-unknown'">
                        {{ node.status ? getStatusLabel(node.status) : 'N/A' }}
                    </span>
                </div>
                <div class="node-hierarchy" *ngIf="node.hierarchy">↳ {{ node.hierarchy }}</div>
            </mat-tree-node>

            <!-- Expandable nodes -->
            <mat-nested-tree-node *matTreeNodeDef="let node; when: hasChild">
                <div class="mat-tree-node">
                    <button mat-icon-button matTreeNodeToggle [attr.aria-label]="'Toggle ' + node.name">
                        <mat-icon class="toggle-icon">
                            {{ hatTreeControl.isExpanded(node) ? 'expand_more' : 'chevron_right' }}
                        </mat-icon>
                    </button>
                    <div class="tree-node" [ngClass]="'node-' + node.type" [class.is-post]="node.partType === 'post'" [class.search-match]="node.matchesSearch" [class.special-rights-node]="node.isSpecialRights">
                        <mat-icon class="node-icon">{{ node.icon }}</mat-icon>
                        <span class="node-name">{{ node.name | normalize }}</span>
                        <span class="node-details" *ngIf="node.details">{{ node.details }}</span>
                        <span *ngIf="node.type === 'policy'" 
                              class="policy-status" 
                              [ngClass]="node.status ? 'status-' + node.status : 'status-unknown'">
                            {{ node.status ? getStatusLabel(node.status) : 'N/A' }}
                        </span>
                        <span class="child-count" *ngIf="node.children?.length && node.type !== 'label'">
                            ({{ node.children.length }})
                        </span>
                    </div>
                    <div class="node-hierarchy" *ngIf="node.hierarchy">↳ {{ node.hierarchy }}</div>
                </div>
                <div class="nested-children" [class.hidden]="!hatTreeControl.isExpanded(node)">
                    <ng-container matTreeNodeOutlet></ng-container>
                </div>
            </mat-nested-tree-node>
        </mat-tree>

        <div class="empty-state" *ngIf="hatDataSource.data.length === 0">
            <mat-icon>search_off</mat-icon>
            <p *ngIf="searchTerm">No results found for "{{ searchTerm }}"</p>
            <p *ngIf="!searchTerm">No hats in this organization</p>
        </div>
    </div>
</mat-dialog-content>

<mat-dialog-actions align="end">
    <button mat-button mat-dialog-close>Close</button>
</mat-dialog-actions>
